/** 项目讨论转待办：预填原文，确认负责人和截止日期后才创建，可选择全公司在职员工。 */
import React, { useEffect, useRef, useState } from "react";
import { ListTodo, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "./ui/sonner";
import { emitTodosChanged } from "../lib/todoSync";

/** discussion 为原讨论，projectId 固定归属项目；onCreated 接收服务端返回的 todoId。 */
export function HubDiscussionTodoDialog({ discussion, projectId, api, zh, user, onClose, onCreated }) {
  const [title, setTitle] = useState((discussion.text || discussion.files?.[0]?.filename || "").replace(/\s+/g, " ").trim().slice(0, 160));
  const [description, setDescription] = useState(discussion.text || "");
  const [assignee, setAssignee] = useState(user?.username || "");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("medium");
  const [assignees, setAssignees] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);

  useEffect(() => {
    let active = true;
    // 负责人范围按项目权限从专用接口读取，保证普通员工也能选跨分公司同事，同时不扩大每日待办的全局派发权限。
    api.get(`/projects/${projectId}/hub/discussion-todo-assignees`).then(({ data }) => { if (active) setAssignees(data.members || []); }).catch((e) => {
      if (active) {
        setAssignees([]);
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : (zh ? "无法加载公司同事名单，请刷新后重试" : "Unable to load coworkers. Please refresh and retry."));
      }
    });
    return () => { active = false; };
  }, [api, projectId, zh]);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!title.trim()) { setError(zh ? "请填写待办标题" : "Enter a title"); return; }
    submitting.current = true;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/projects/${projectId}/hub/discussions/${discussion.id}/todo`, {
        title: title.trim(), description, assignee, dueDate: dueDate || null, priority,
      });
      emitTodosChanged("hub-discussion");
      toast.success(zh ? "已转为待办" : "To-do created");
      onCreated(data.todoId);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (zh ? "创建失败，请重试" : "Unable to create. Please retry."));
    } finally { submitting.current = false; setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      {/* 项目讨论可全屏到 z-60；表单仍需位于其上，并保留 Radix 的焦点和 Esc 处理。 */}
      <DialogContent className="z-[71] max-w-lg" overlayClassName="z-[70]" closeDisabled={busy}
        closeLabel={zh ? "关闭转待办" : "Close to-do form"} data-testid="hub-disc-todo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><ListTodo className="h-4 w-4 text-brand-600" />{zh ? "转为待办" : "Create a to-do"}</DialogTitle>
          <DialogDescription>{zh ? "将这条讨论加入每日待办。" : "Add this discussion to Daily To-Dos."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <label htmlFor="hub-disc-todo-title" className="text-xs font-medium text-slate-700">{zh ? "待办标题" : "Title"}</label>
            <Input id="hub-disc-todo-title" data-testid="hub-disc-todo-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} required disabled={busy} />
          </div>
          <div className="space-y-1">
            <label htmlFor="hub-disc-todo-description" className="text-xs font-medium text-slate-700">{zh ? "待办内容" : "Description"}</label>
            <Textarea id="hub-disc-todo-description" data-testid="hub-disc-todo-description" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} disabled={busy} />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="hub-disc-todo-assignee" className="text-xs font-medium text-slate-700">{zh ? "负责人" : "Assignee"}</label>
              <select id="hub-disc-todo-assignee" data-testid="hub-disc-todo-assignee" className="h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm" value={assignee} onChange={(e) => setAssignee(e.target.value)} disabled={busy || !assignees}>
                <option value={user?.username}>{zh ? "我自己" : "Myself"}</option>
                {(assignees || []).filter((m) => m.username !== user?.username).map((m) => <option key={m.username} value={m.username}>{m.name || m.username}{m.branch ? ` · ${m.branch}` : ""}</option>)}
              </select>
              <p className="text-[11px] text-slate-500">{zh ? "可选择全公司各分公司的在职同事" : "Choose any active coworker across company branches"}</p>
            </div>
            <div className="space-y-1">
              <label htmlFor="hub-disc-todo-due" className="text-xs font-medium text-slate-700">{zh ? "截止日期（可选）" : "Due date (optional)"}</label>
              <Input id="hub-disc-todo-due" data-testid="hub-disc-todo-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} disabled={busy} />
            </div>
          </div>
          <div className="space-y-1">
            <label htmlFor="hub-disc-todo-priority" className="text-xs font-medium text-slate-700">{zh ? "优先级" : "Priority"}</label>
            <select id="hub-disc-todo-priority" data-testid="hub-disc-todo-priority" className="h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm" value={priority} onChange={(e) => setPriority(e.target.value)} disabled={busy}>
              {[["low", "低", "Low"], ["medium", "中", "Medium"], ["high", "高", "High"], ["urgent", "紧急", "Urgent"]].map(([v, z, en]) => <option key={v} value={v}>{zh ? z : en}</option>)}
            </select>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {/* 手机右下角悬浮通话按钮会遮住弹窗操作区；窄屏预留空间，确保提交按钮可见且可点。 */}
          <div className="flex justify-end gap-2 pr-14 sm:pr-0">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>{zh ? "取消" : "Cancel"}</Button>
            <Button type="submit" disabled={busy} data-testid="hub-disc-todo-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{zh ? "创建待办" : "Create to-do"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
